import { type ICellRendererComp } from '@ag-grid-community/core'
import { renderToStaticMarkup } from 'react-dom/server'
import { Box, type BoxProps, Row } from '../../Box'
import { Image } from '../../Image'
import { fallbackLogoCss, logoPositionCss } from './ProjectCellRenderer.css'
import {
  type RendererParams,
  isFilterCellRenderer,
  isGroupCellRenderer,
  isGroupRow,
} from './utils'

const IMAGE_CDN = 'https://static1.tokenterminal.com/'

type WithLogoCellRendererParams<T> = RendererParams<T, string>

export class WithLogoCellRenderer<
  T extends Record<string, string | undefined>,
  U extends Record<string, string | undefined>,
> implements ICellRendererComp<T>
{
  protected template: ChildNode = getWithLogoRowTemplate() as ChildNode
  protected eGui?: HTMLElement

  protected textField: keyof U
  protected logoField: keyof U
  protected originalLogoSrc!: string
  protected originalLogoSrcset!: string

  constructor({
    textField,
    logoField,
  }: {
    textField: keyof U
    logoField: keyof U
  }) {
    this.textField = textField
    this.logoField = logoField
  }

  // gets called once before the renderer is used
  init(params: WithLogoCellRendererParams<T>): void {
    // create the cell
    this.eGui = this.template.cloneNode(true) as HTMLElement

    const imgEl = this.eGui.querySelector(
      '.cell-logo img',
    ) as HTMLImageElement | null

    if (imgEl) {
      this.originalLogoSrc = imgEl.src
      this.originalLogoSrcset = imgEl.srcset
    }

    this.refresh(params)
  }

  getGui() {
    return this.eGui as HTMLElement
  }

  // gets called whenever the cell refreshes
  refresh(params: WithLogoCellRendererParams<T>) {
    if (!this.eGui) {
      return false
    }

    const imgEl = this.eGui.querySelector('.cell-logo') as HTMLElement | null
    const fallbackLogo = this.eGui.querySelector(
      '.fallback-logo',
    ) as HTMLElement | null

    const data: { title: string; logo: string | null } = {
      title: params.value ? params.value : 'Unknown',
      logo: null,
    }

    let refData: Record<string, U> | undefined = params.colDef?.refData as
      | Record<string, U>
      | undefined

    if (!refData && params.colDef) {
      refData = params.columnApi.getColumn(params.colDef.field)?.getColDef()
        ?.refData as Record<string, U> | undefined
    }

    if (!refData && !isFilterCellRenderer(params) && params.node?.field) {
      refData = params.columnApi.getColumn(params.node.field)?.getColDef()
        ?.refData as Record<string, U> | undefined
    }

    if (refData && params.value && refData[params.value]) {
      const refInfo = (refData as unknown as Record<string, U>)[params.value]
      data.title = refInfo[this.textField] ?? params.value ?? 'Unknown'
      data.logo = refInfo[this.logoField] ?? null
    }

    // remove the logo completely visually
    if (
      // This seems to be a bug, sometimes the filterCellParams are not passed
      params.value === '(Select All)' ||
      (isFilterCellRenderer(params) && params.value === null) ||
      (!isGroupCellRenderer(params) && isGroupRow(params))
    ) {
      this.hideLogo(imgEl, fallbackLogo)

      if (fallbackLogo) {
        fallbackLogo.style.opacity = '0'
      }
    } else {
      if (data.logo) {
        this.showLogo(imgEl, fallbackLogo, data.logo)
      } else {
        this.hideLogo(imgEl, fallbackLogo)
      }
    }

    // hide the text when row is a group row
    if (!isGroupCellRenderer(params) && isGroupRow(params)) {
      data.title = ''
    }

    this.eGui.childNodes[1].textContent = data.title

    // return true to tell the grid we refreshed successfully
    return true
  }

  showLogo(
    imgEl: HTMLElement | null,
    fallbackLogo: HTMLElement | null,
    logo: string,
  ): void {
    if (!imgEl || !fallbackLogo) {
      return
    }

    if (!this.originalLogoSrc || !this.originalLogoSrcset) {
      this.hideLogo(imgEl, fallbackLogo)
    }

    const encodedCdn = encodeURIComponent(IMAGE_CDN)
    // show the logo
    imgEl.style.opacity = '1'
    // hide fallbackLogo
    fallbackLogo.style.opacity = '0'

    const rawImgEl = imgEl.querySelector('img')
    if (rawImgEl) {
      rawImgEl.src = this.originalLogoSrc.replace(
        new RegExp(encodedCdn + '[^&]+', 'g'),
        encodedCdn + logo,
      )
      rawImgEl.srcset = this.originalLogoSrcset.replace(
        new RegExp(encodedCdn + '[^&]+', 'g'),
        encodedCdn + logo,
      )
    }
  }

  hideLogo(imgEl: HTMLElement | null, fallbackLogo: HTMLElement | null): void {
    if (!imgEl || !fallbackLogo) {
      return
    }

    const rawImgEl = imgEl.querySelector('img')
    rawImgEl?.setAttribute(
      'src',
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
    )
    rawImgEl?.removeAttribute('srcset')
    // hide the logo
    imgEl.style.opacity = '0'
    // show fallbackLogo
    fallbackLogo.style.opacity = '1'
  }
}

function FallbackLogo({ className }: Pick<BoxProps, 'className'>) {
  return (
    <Box
      as="span"
      aria-hidden="true"
      title="No logo available"
      className={[className, fallbackLogoCss, 'fallback-logo']}
    />
  )
}

function getWithLogoRowTemplate() {
  if (typeof window === 'undefined') {
    return null
  }

  const parser = new DOMParser()

  const chainRowTemplate = parser.parseFromString(
    renderToStaticMarkup(
      <Row gap="xs" start>
        <Box marginRight="xxs" display="grid" style={{ width: 14, height: 14 }}>
          <Image
            src={`${IMAGE_CDN}unknown.png`}
            alt="Logo"
            height="14"
            width="14"
            borderRadius="rounded"
            style={{ height: 14 }}
            className={['cell-logo', logoPositionCss]}
          />
          <FallbackLogo className={logoPositionCss} />
        </Box>
        %%VALUE%%
      </Row>,
    ),
    'text/html',
  )

  return chainRowTemplate.body.firstChild as ChildNode
}
